import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Box, Card, CardContent, IconButton, Tooltip, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import Swal from 'sweetalert2';
import { EventoTareaDialog } from './EventoTareaDialog';
import { finalizarCitaCalendario, reactivarCitaCalendario } from '../../services/apis';
import { textoMayusculas } from '../../utils/utils';

export const TarjetaTarea = ({ id, tarea, overlay = false, onChanged }) => {
    const esCita = tarea.tipo_calendario === 'cita';
    const citaFinalizada = esCita && Number(tarea.finalizado || 0) === 1;
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        disabled: overlay,
    });
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const accentColor = tarea.color_estado || '#e53935';

    const style = {
        transform: !overlay && transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        zIndex: overlay ? 99999 : transform ? 1000 : 1,
        opacity: !overlay && isDragging ? 0.25 : 1,
        width: '100%',
        pointerEvents: overlay ? 'none' : undefined,
    };

    const handleFinalizarCita = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!esCita || citaFinalizada || saving) return;

        const confirm = await Swal.fire({
            icon: 'question',
            title: 'Finalizar cita',
            text: 'La cita quedara marcada como completada en el calendario.',
            showCancelButton: true,
            confirmButtonText: 'Finalizar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2e7d32',
        });

        if (!confirm.isConfirmed) return;

        setSaving(true);
        const response = await finalizarCitaCalendario(tarea.id_cita || tarea.id);
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo finalizar',
                text: response?.message || 'Revisa la cita e intentalo de nuevo.',
            });
            return;
        }

        if (onChanged) {
            await onChanged();
        }
    };

    const handleReactivarCita = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!esCita || !citaFinalizada || saving) return;

        const confirm = await Swal.fire({
            icon: 'question',
            title: 'Reactivar cita',
            text: 'La cita volvera a quedar agendada en el calendario.',
            showCancelButton: true,
            confirmButtonText: 'Reactivar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1976d2',
        });

        if (!confirm.isConfirmed) return;

        setSaving(true);
        const response = await reactivarCitaCalendario(tarea.id_cita || tarea.id);
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo reactivar',
                text: response?.message || 'Revisa la cita e intentalo de nuevo.',
            });
            return;
        }

        if (onChanged) {
            await onChanged();
        }
    };

    return (
        <>
            <div
                ref={setNodeRef}
                style={style}
                data-calendario-evento="true"
                onPointerDownCapture={(event) => {
                    if (event.target.closest('button')) {
                        event.stopPropagation();
                    }
                }}
            >
                <Card
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                        borderRadius: esCita ? '4px' : '10px',
                        border: esCita
                            ? citaFinalizada
                                ? '1px solid rgba(46,125,50,0.25)'
                                : '1px solid rgba(255,255,255,0.16)'
                            : `1px solid ${accentColor}44`,
                        background: esCita
                            ? citaFinalizada
                                ? '#edf6f0'
                                : '#383838'
                            : `linear-gradient(180deg, ${accentColor}24 0%, ${accentColor}14 100%)`,
                        boxShadow: esCita ? 'none' : '0 7px 16px rgba(229,57,53,0.08)',
                        opacity: citaFinalizada ? 0.86 : 1,
                        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                        '&:hover': {
                            transform: esCita || citaFinalizada ? 'none' : 'translateY(-2px)',
                            boxShadow: esCita ? 'none' : '0 10px 20px rgba(229,57,53,0.12)',
                        },
                    }}
                >
                    <CardContent sx={{ p: esCita ? '8px 10px !important' : '8px 10px !important' }}>
                        <Box sx={{ display: 'grid', gap: esCita ? 0 : 0.55 }}>
                            {esCita ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 0.75,
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            justifyItems: 'center',
                                            alignContent: 'start',
                                            gap: 0.25,
                                            flexShrink: 0,
                                            width: 22,
                                        }}
                                    >
                                        <Tooltip title={citaFinalizada ? 'Reactivar cita' : 'Finalizar cita'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onPointerDown={(event) => event.stopPropagation()}
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                    onClick={citaFinalizada ? handleReactivarCita : handleFinalizarCita}
                                                    disabled={saving}
                                                    sx={{
                                                        color: citaFinalizada ? '#1976d2' : '#21c77a',
                                                        p: 0.1,
                                                    }}
                                                >
                                                    {citaFinalizada ? (
                                                        <UndoIcon sx={{ fontSize: 18 }} />
                                                    ) : (
                                                        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        <Tooltip title="Ver detalle">
                                            <IconButton
                                                size="small"
                                                onPointerDown={(event) => event.stopPropagation()}
                                                onMouseDown={(event) => event.stopPropagation()}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setOpen(true);
                                                }}
                                                sx={{ color: '#d8d8d8', p: 0.1 }}
                                            >
                                                <InfoIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <Box
                                        {...attributes}
                                        {...listeners}
                                        sx={{
                                            minWidth: 0,
                                            flex: 1,
                                            cursor: 'grab',
                                            pt: 0.1,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: citaFinalizada ? '#3d5b47' : '#fff',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: 1.25,
                                                display: '-webkit-box',
                                                overflow: 'hidden',
                                                WebkitBoxOrient: 'vertical',
                                                WebkitLineClamp: tarea.trabajadores_asignados || tarea.cliente ? 2 : 3,
                                            }}
                                        >
                                            {tarea.hora_inicio ? `${tarea.hora_inicio} - ` : ''}{tarea.titulo}
                                        </Typography>

                                        {tarea.cliente && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.35,
                                                    minWidth: 0,
                                                    mt: 0.15,
                                                    opacity: citaFinalizada ? 0.8 : 0.9,
                                                }}
                                            >
                                                <PersonIcon
                                                    sx={{
                                                        fontSize: 11,
                                                        color: citaFinalizada ? '#3d5b47' : '#cfcfcf',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        color: citaFinalizada ? '#3d5b47' : '#d8d8d8',
                                                        fontSize: 10,
                                                        lineHeight: 1.1,
                                                        fontWeight: 600,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {textoMayusculas(tarea.cliente)}
                                                </Typography>
                                            </Box>
                                        )}

                                        {tarea.trabajadores_asignados && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.35,
                                                    minWidth: 0,
                                                    mt: 0.15,
                                                    opacity: citaFinalizada ? 0.8 : 0.9,
                                                }}
                                            >
                                                <GroupsIcon
                                                    sx={{
                                                        fontSize: 11,
                                                        color: citaFinalizada ? '#3d5b47' : '#cfcfcf',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        color: citaFinalizada ? '#3d5b47' : '#d8d8d8',
                                                        fontSize: 10,
                                                        lineHeight: 1.1,
                                                        fontWeight: 600,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {tarea.trabajadores_asignados}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                                <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 0.8,
                                }}
                            >
                                <Box
                                    {...attributes}
                                    {...listeners}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.55,
                                        minWidth: 0,
                                        cursor: 'grab',
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 14, color: '#555' }} />
                                    <Typography fontSize={12} fontWeight={700} noWrap>
                                        {tarea.hora_inicio} - {tarea.hora_final}
                                    </Typography>
                                </Box>

                                {/* Boton de informacion comentado en citas para dejar la estructura tipo lista de recordatorios. */}
                                <Tooltip title="Ver detalle">
                                    <IconButton
                                        size="small"
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setOpen(true);
                                        }}
                                        sx={{ color: accentColor, p: 0.2 }}
                                    >
                                        <InfoIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography fontSize={12.5} fontWeight={700} noWrap>
                                #{tarea.id} {tarea.titulo}
                            </Typography>

                            {tarea.proyecto && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
                                    <FolderIcon sx={{ fontSize: 13, color: '#666' }} />
                                    <Typography fontSize={11.8} color="#555" noWrap>
                                        {tarea.proyecto}
                                    </Typography>
                                </Box>
                            )}

                            {tarea.cliente && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
                                    <PersonIcon sx={{ fontSize: 13, color: '#666' }} />
                                    <Typography fontSize={11.8} color="#555" noWrap>
                                        {textoMayusculas(tarea.cliente)}
                                    </Typography>
                                </Box>
                            )}
                                </>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </div>

            {/* Dialog de informacion extra disponible para citas, pero oculto visualmente por ahora. */}
            <EventoTareaDialog
                open={open}
                onClose={() => setOpen(false)}
                evento={tarea}
                onChanged={onChanged}
            />
        </>
    );
};
